import { forwardRef, type ButtonHTMLAttributes } from 'react'

import { cn } from '../../lib/cn'

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost'
type ButtonSize = 'sm' | 'md' | 'icon'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
}

const variants: Record<ButtonVariant, string> = {
  primary:
    'border border-moss bg-moss text-white shadow-sm hover:border-[#26483c] hover:bg-[#26483c] disabled:border-moss/50 disabled:bg-moss/50',
  secondary:
    'border border-ink/15 bg-white text-ink shadow-sm hover:border-ink/30 hover:bg-mist/60 disabled:text-ink/40',
  danger:
    'border border-red-700 bg-red-700 text-white shadow-sm hover:border-red-800 hover:bg-red-800 disabled:border-red-300 disabled:bg-red-300',
  ghost: 'border border-transparent bg-transparent text-ink/70 hover:bg-ink/5 hover:text-ink',
}

const sizes: Record<ButtonSize, string> = {
  sm: 'min-h-9 rounded-lg px-3 text-sm',
  md: 'min-h-11 rounded-xl px-4 text-sm',
  icon: 'h-10 w-10 rounded-xl p-0',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex shrink-0 items-center justify-center gap-2 font-semibold transition disabled:cursor-not-allowed disabled:opacity-70',
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    />
  ),
)

Button.displayName = 'Button'
